// BetterAuth client — Phase Auth-Migration-1 (Phase 4 final).
//
// Points at /api/auth/* (BetterAuth-owned post Phase 4). The legacy
// `useBetterAuth` feature flag is gone; BetterAuth is the only auth.
//
// Usage:
//
//   import { authClient } from '~/lib/auth-client'
//
//   const { data, error } = await authClient.signIn.email({
//     email,
//     password,
//   })
//
//   const { data, error } = await authClient.signIn.social({
//     provider: 'google',
//     callbackURL: window.location.origin + '/en/jobs',
//   })
//
// To enable passkeys/magic-links/MFA later (Phase 1.5+), wire the
// matching client plugin from better-auth/client/plugins/* here.

import { createAuthClient } from "better-auth/react";
import { magicLinkClient, twoFactorClient } from "better-auth/client/plugins";
// Passkey is a separate package on both server and client.
import { passkeyClient } from "@better-auth/passkey/client";
import { SUPPORTED_LOCALES, DEFAULT_LOCALE } from "~/utils/resolve-locale";

// B-new-937 — the 2FA challenge lives at `/{locale}/2fa-challenge`.
//
// `/{locale}/auth/2fa-challenge` is a PHANTOM path: no route file, no entry
// in routeTree.gen.ts. The SPA catch-all answers it with a 200 and bounces
// the user to the locale home, so anything targeting it fails SILENTLY and
// still looks healthy to a status-code check. Never point here.
const TWO_FACTOR_CHALLENGE_PATH = "2fa-challenge";

/**
 * The locale segment of the URL the user is on right now.
 *
 * The app is locale-routed (`/$locale/...`), so hardcoding `en` would throw
 * a Spanish or German user into an English challenge screen mid-sign-in.
 * Reads the first path segment and keeps it only if it is one of the eight
 * supported locales; `en` otherwise.
 *
 * The `typeof` guards are load-bearing, not defensive noise: this runs inside
 * a fetch hook that BetterAuth may invoke in a non-DOM context, and
 * `window.location` can be stubbed by callers/tests.
 */
function currentLocale(): string {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  const pathname = window.location?.pathname;
  if (typeof pathname !== "string") return DEFAULT_LOCALE;
  const firstSegment = pathname.split("/")[1];
  return (SUPPORTED_LOCALES as readonly string[]).includes(firstSegment)
    ? firstSegment
    : DEFAULT_LOCALE;
}

// BetterAuth requires an ABSOLUTE base URL. A relative one ("/api/auth")
// makes createAuthClient throw `BetterAuthError: Invalid base URL` — and
// because that happens at MODULE IMPORT time, the throw propagates before
// React renders anything, so the entire app is a blank white page with only
// a cryptic console line to go on.
//
// The previous fallback was `import.meta.env.VITE_API_URL || ""`, which
// produced exactly that: a missing env var silently became an invalid
// config. Observed 2026-09-05 on a fresh checkout — `apps/web/.env` does not
// exist (it is gitignored; only `.env.example` is tracked), so every
// first-run `pnpm dev` blanked /en/sign-in.
//
// Falling back to the current origin is strictly better than "":
//   - in local dev it is CORRECT — Vite proxies /api and /trpc to
//     http://localhost:8787 (see vite.config.ts) and forwards cookie +
//     authorization headers, so same-origin requests reach the API;
//   - in a misconfigured deployed build it degrades to diagnosable 404s
//     against the app origin instead of a white screen.
// Either way the app renders and the failure is legible.
const configuredApiUrl = import.meta.env.VITE_API_URL;
const originFallback =
  typeof window !== "undefined" ? window.location.origin : "";
const apiUrl = configuredApiUrl || originFallback;

if (!configuredApiUrl && typeof console !== "undefined") {
  // Loud, actionable, and names the variable — the thing the old failure mode
  // never did.
  console.error(
    "[auth-client] VITE_API_URL is not set; falling back to " +
      `"${originFallback}". For local dev: copy apps/web/.env.example to ` +
      "apps/web/.env. VITE_API_URL must be the WEB origin " +
      "(http://localhost:3001) so requests go through Vite's /api proxy to " +
      ":8787 — NOT the API port directly, which would bypass the proxy's " +
      "cookie forwarding. In a deployed build this means the build-time env " +
      "was missing.",
  );
}

export const authClient = createAuthClient({
  // Phase 4: BetterAuth owns /api/auth/* directly (Lucia routes removed).
  baseURL: `${apiUrl}/api/auth`,
  fetchOptions: {
    credentials: "include",
  },
  plugins: [
    // WebAuthn / passkeys. Use authClient.passkey.addPasskey() to enrol
    // and authClient.signIn.passkey() to authenticate.
    passkeyClient(),
    // Magic links: authClient.signIn.magicLink({ email, callbackURL })
    magicLinkClient(),
    // TOTP 2FA: authClient.twoFactor.enable() / verifyTotp() / disable()
    //
    // B-new-937: this MUST be given a redirect strategy. Constructed bare
    // (as it was), the plugin's `fetchPlugins[].hooks.onSuccess` sees the
    // `{ twoFactorRedirect: true }` response, finds neither option set, and
    // silently does nothing — while the API has ALREADY deleted the
    // pre-2FA session. The user is left sitting on the sign-in page, signed
    // out, with no error and no way forward. That is the lockout.
    //
    // This path is the CLIENT-FETCH sign-in, in practice passkey
    // (`/passkey/verify-authentication`). OAuth and magic-link are top-level
    // browser navigations the server redirects, so they never reach this hook.
    //
    // `onTwoFactorRedirect` and not `twoFactorPage`, for two reasons:
    //   1. `twoFactorPage` is a STATIC string captured at module-import time.
    //      It cannot carry the locale the user is actually browsing in, and
    //      in an SPA that locale can change after import.
    //   2. In better-auth 1.6.11 the hook `return`s immediately after
    //      awaiting `onTwoFactorRedirect`, so a `twoFactorPage` set alongside
    //      it would be unreachable dead config.
    // Verified against node_modules/better-auth/dist/plugins/two-factor/
    // client.d.mts (option names) and client.mjs (the precedence above).
    twoFactorClient({
      onTwoFactorRedirect: () => {
        if (typeof window === "undefined") return;
        const destination = `/${currentLocale()}/${TWO_FACTOR_CHALLENGE_PATH}`;
        // A full-page assignment, deliberately: auth-client.ts is a plain
        // module with no router instance to navigate with, and the session
        // state has just changed server-side, so a clean reload is correct.
        window.location.href = destination;
      },
    }),
  ],
});

// React hooks BetterAuth exposes via the createAuthClient return value.
// Re-exported here so call sites can stay on the `~/lib/auth-client` path
// even if we add plugins later that add more named exports.
export const { useSession, signIn, signUp, signOut } = authClient;
